import React, { useEffect, useMemo } from 'react';
import { useArrowStore, Arrow } from '@/stores/arrowStore';
import { useCanvasStore } from '@/stores/canvasStore';
import { ArrowPath, getConnectorPoint } from './ArrowPath';
import { ArrowDraftPath } from './ArrowDraftPath';

interface ArrowLayerProps {
  zoom: number;
  panX: number;
  panY: number;
}

export function ArrowLayer({ zoom, panX, panY }: ArrowLayerProps) {
  const { 
    arrows, 
    selectedArrowId, 
    setSelectedArrow,
    arrowDraft,
    isDrawing,
    fetchArrows,
  } = useArrowStore();
  
  const { items, currentBoardId } = useCanvasStore();
  
  // Fetch arrows when board changes
  useEffect(() => {
    if (currentBoardId) {
      fetchArrows(currentBoardId);
    }
  }, [currentBoardId, fetchArrows]);

  // Pre-compute sibling info for arrows sharing the same connector.
  // This enables fan-out so arrows don't overlap when multiple leave the same point.
  const siblingInfo = useMemo(() => {
    const result = new Map<string, { startIndex: number; startCount: number; endIndex: number; endCount: number }>();

    // Initialize all arrows with default (no siblings)
    for (const arrow of arrows) {
      result.set(arrow.id, { startIndex: 0, startCount: 1, endIndex: 0, endCount: 1 });
    }

    // Group by shared start connector
    const startGroups = new Map<string, Arrow[]>();
    for (const arrow of arrows) {
      if (arrow.start_item_id && arrow.start_connector) {
        const key = `${arrow.start_item_id}:${arrow.start_connector}`;
        if (!startGroups.has(key)) startGroups.set(key, []);
        startGroups.get(key)!.push(arrow);
      }
    }

    // Group by shared end connector
    const endGroups = new Map<string, Arrow[]>();
    for (const arrow of arrows) {
      if (arrow.end_item_id && arrow.end_connector) {
        const key = `${arrow.end_item_id}:${arrow.end_connector}`;
        if (!endGroups.has(key)) endGroups.set(key, []);
        endGroups.get(key)!.push(arrow);
      }
    }

    // Helper: get endpoint coordinates for an arrow
    const getEndCoords = (arrow: Arrow): { x: number; y: number } | null => {
      if (arrow.end_item_id && arrow.end_connector) {
        const endItem = items.find(i => i.id === arrow.end_item_id);
        if (endItem) return getConnectorPoint(endItem, arrow.end_connector);
      }
      if (arrow.end_x !== null && arrow.end_y !== null) {
        return { x: arrow.end_x, y: arrow.end_y };
      }
      return null;
    };

    // Helper: get start point coordinates for an arrow
    const getStartCoords = (arrow: Arrow): { x: number; y: number } | null => {
      if (arrow.start_item_id && arrow.start_connector) {
        const startItem = items.find(i => i.id === arrow.start_item_id);
        if (startItem) return getConnectorPoint(startItem, arrow.start_connector);
      }
      if (arrow.start_x !== null && arrow.start_y !== null) {
        return { x: arrow.start_x, y: arrow.start_y };
      }
      return null;
    };

    // Process start groups: sort by angle to each arrow's endpoint
    for (const [, group] of startGroups) {
      if (group.length <= 1) continue;

      const startPt = getStartCoords(group[0]);
      if (!startPt) continue;

      const sorted = [...group].sort((a, b) => {
        const endA = getEndCoords(a);
        const endB = getEndCoords(b);
        if (!endA || !endB) return 0;
        const angleA = Math.atan2(endA.y - startPt.y, endA.x - startPt.x);
        const angleB = Math.atan2(endB.y - startPt.y, endB.x - startPt.x);
        return angleA - angleB;
      });

      sorted.forEach((arrow, index) => {
        const info = result.get(arrow.id)!;
        info.startIndex = index;
        info.startCount = sorted.length;
      });
    }

    // Process end groups: sort by angle from each arrow's start point
    for (const [, group] of endGroups) {
      if (group.length <= 1) continue;

      const endPt = getEndCoords(group[0]);
      if (!endPt) continue;

      const sorted = [...group].sort((a, b) => {
        const startA = getStartCoords(a);
        const startB = getStartCoords(b);
        if (!startA || !startB) return 0;
        const angleA = Math.atan2(startA.y - endPt.y, startA.x - endPt.x);
        const angleB = Math.atan2(startB.y - endPt.y, startB.x - endPt.x);
        return angleA - angleB;
      });

      sorted.forEach((arrow, index) => {
        const info = result.get(arrow.id)!;
        info.endIndex = index;
        info.endCount = sorted.length;
      });
    }

    return result;
  }, [arrows, items]);
  
  const handleArrowClick = (arrowId: string) => {
    setSelectedArrow(arrowId);
  };
  
  const handleBackgroundClick = (e: React.MouseEvent) => {
    // Only deselect if clicking on the SVG background, not on an arrow
    if (e.target === e.currentTarget) {
      setSelectedArrow(null);
    }
  };
  
  return (
    <svg
      className="absolute inset-0 pointer-events-none overflow-visible"
      style={{ 
        zIndex: 10,
        transform: `scale(${zoom}) translate(${panX}px, ${panY}px)`,
        transformOrigin: 'top left',
      }}
      onClick={handleBackgroundClick}
    >
      {/* Render saved arrows */}
      {arrows.map((arrow) => (
        <g key={arrow.id} className="pointer-events-auto">
          <ArrowPath
            arrow={arrow}
            items={items}
            isSelected={selectedArrowId === arrow.id}
            onClick={() => handleArrowClick(arrow.id)}
            siblingInfo={siblingInfo.get(arrow.id)}
          />
        </g>
      ))}
      
      {/* Render draft arrow while drawing */}
      {isDrawing && arrowDraft && (
        <ArrowDraftPath draft={arrowDraft} items={items} />
      )}
    </svg>
  );
}
